import { API_VERSION } from "@next-platform/client-common-core";
import { IPurchaseOptions } from "@src/types";
import { useEffect, useState } from "react";

import appLogger from "@next-platform/client-common-core/logger/appLogger";

import useApi from "../components/useApi";

const purchaseOptionsLogger = appLogger.withTag("usePurchaseOptions");

const PURCHASE_OPTIONS_RESOURCE = "subscriptions/purchase_options";

type UsePurchaseOptionsArgs = {
  initialData?: IPurchaseOptions;
  countryOverride?: string | null;
};

type PurchaseOptionsFetchResult = {
  fetchedForOverride: string | null;
  purchaseOptions: IPurchaseOptions | null;
};

export default function usePurchaseOptions({ initialData, countryOverride = null }: UsePurchaseOptionsArgs = {}) {
  const { jsonApiService, callApi: callGetPurchaseOptionsApi, isLoading: isApiLoading } = useApi<IPurchaseOptions>();

  // The stored result is tagged with the override it was fetched for. Comparing
  // that tag to the current override (during render) tells us whether the data
  // belongs to the active country — no leftover outcome can be mistaken for the
  // current one across a country switch.
  const [stateFetchResult, setStateFetchResult] = useState<PurchaseOptionsFetchResult | null>(null);

  // A country override (e.g. RU for VPN users) must re-query even when initial
  // data was handed over from the Subscribe page; otherwise we only fetch when
  // nothing was provided up front.
  const shouldFetchPurchaseOptions = Boolean(countryOverride) || !initialData;

  useEffect(() => {
    if (!shouldFetchPurchaseOptions) {
      return;
    }

    // React's standard stale-response guard: the cleanup flips isActive to false
    // before the effect re-runs (override changed, request superseded), so a
    // late response from the previous override can never overwrite the state.
    let isActive = true;
    const queryParams: Record<string, string> = countryOverride ? { payment_country_override: countryOverride } : {};
    callGetPurchaseOptionsApi(jsonApiService.fetchResource, PURCHASE_OPTIONS_RESOURCE, null, queryParams, API_VERSION.V2)
      .then((freshPurchaseOptions) => {
        if (isActive) {
          setStateFetchResult({ fetchedForOverride: countryOverride, purchaseOptions: freshPurchaseOptions ?? null });
        }
      })
      .catch((fetchError) => {
        purchaseOptionsLogger.error("Failed to fetch purchase options", fetchError);
        // Record the failure so the loading state terminates instead of spinning
        // forever waiting for data.
        if (isActive) {
          setStateFetchResult({ fetchedForOverride: countryOverride, purchaseOptions: null });
        }
      });

    return () => {
      isActive = false;
    };
  }, [callGetPurchaseOptionsApi, jsonApiService, shouldFetchPurchaseOptions, countryOverride]);

  // Only honour the stored result when it was fetched for the active override.
  const hasResultForActiveOverride = stateFetchResult !== null && stateFetchResult.fetchedForOverride === countryOverride;
  const fetchedForActiveOverride = hasResultForActiveOverride ? stateFetchResult.purchaseOptions : null;

  // With an override active we never fall back to initial data (it reflects the
  // geo-detected, wrong country); only the override's own result is valid.
  // Without an override the provided initial data wins so switching back to a
  // non-override country is not stale.
  const purchaseOptions: IPurchaseOptions | null = countryOverride ? fetchedForActiveOverride : (initialData ?? fetchedForActiveOverride);

  // Loading spans both the in-flight fetch and the gap between an override change
  // and its result landing — hasResultForActiveOverride is false the moment the
  // override changes, closing the window where checkout could render the wrong
  // methods. Gating on shouldFetchPurchaseOptions stops an orphaned override
  // request (e.g. leaving RU before it resolves) from keeping the checkout
  // spinning once valid initialData is the active source again.
  const isPurchaseOptionsLoading = shouldFetchPurchaseOptions && (isApiLoading || !hasResultForActiveOverride);

  return { purchaseOptions, isPurchaseOptionsLoading };
}
